import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { useOwnMainCard } from "@/cards/common/api/taascard.api";
import { invalidateDeckMainCardByIdQueries } from "@/cards/common/utils/deckMainCardByIdQuery";

type UseCollectImpactCardOptions = {
  mainCardId?: number | null;
};

function apiErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const message = data?.message ?? data?.detail ?? data?.error;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (Array.isArray(message) && typeof message[0] === "string") return message[0];
  return (error as Error)?.message || "Could not collect this Card. Try again.";
}

export function useCollectImpactCard({ mainCardId }: UseCollectImpactCardOptions) {
  const queryClient = useQueryClient();
  const cardId = Number(mainCardId) || 0;
  const [justCollected, setJustCollected] = useState(false);

  const { update, isLoading: isCollecting } = useOwnMainCard({
    key: ["own-main-card", cardId],
  });

  useEffect(() => {
    setJustCollected(false);
  }, [cardId]);

  const collect = async () => {
    if (!cardId) {
      throw new Error("This Card is unavailable.");
    }

    try {
      await update({ main_card_id: cardId });
    } catch (error) {
      const message = apiErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
      throw new Error(message);
    }

    setJustCollected(true);
    invalidateDeckMainCardByIdQueries(queryClient, cardId);
    void queryClient.invalidateQueries({ queryKey: ["card-thumbnail-by-id"] });
    void queryClient.invalidateQueries({ queryKey: ["deck-card-thumbnails"] });
    void queryClient.invalidateQueries({ queryKey: ["charm-collected-card-thumbnails"] });
    void queryClient.invalidateQueries({ queryKey: ["deck-card-thumbnails-public"] });
    enqueueSnackbar("This Impact Card is now in your collection.", { variant: "success" });
  };

  return {
    collect,
    isCollecting,
    justCollected,
  };
}
